import { decamelizeKeys } from 'humps'
import _ from 'lodash/fp'
import { all, call, put, takeLatest } from 'redux-saga/effects'
import { types as teesTypes, actions as teesActions } from '../reducers/tees'
import { getCurrentClubId } from './network'
import { apolloClient } from '@fe/business/src/apollo'
import { CLUB_TEES_QUERY, UPDATE_TEE_MUTATION, CREATE_TEE_MUTATION, DELETE_TEE_MUTATION } from './mutations/tees'

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

export function * loadTeesEffect () {
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.query], {
      query: CLUB_TEES_QUERY,
      variables: { clubId },
      fetchPolicy: 'network-only'
    })
    const tees = _.getOr([], 'data.clubTees', response)
    return decamelizeKeys(tees)
  } catch (error) {
    return { error }
  }
}

export function * createTeeEffect (payload) {
  try {
    // payload.club_id is never populated - clubCourses doesn't select clubId on Course - use the route instead
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: CREATE_TEE_MUTATION,
      variables: {
        input: {
          clubId,
          courseId: _.get('course_id', payload),
          name: _.get('name', payload),
          color: _.get('color', payload),
          slopeRating: _.get('slope_rating', payload),
          courseRating: _.get('course_rating', payload)
        }
      }
    })
    return decamelizeKeys(_.get('data.createTee', response))
  } catch (error) {
    return { error }
  }
}

export function * saveTeesEffect (payload) {
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: UPDATE_TEE_MUTATION,
      variables: {
        input: {
          clubId,
          id: _.get('id', payload),
          name: _.get('name', payload),
          color: _.get('color', payload),
          slopeRating: _.get('slope_rating', payload),
          courseRating: _.get('course_rating', payload)
        }
      }
    })
    return decamelizeKeys(_.get('data.updateTee', response))
  } catch (error) {
    return { error }
  }
}

export function * deleteTeeEffect (teeId) {
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: DELETE_TEE_MUTATION,
      variables: { clubId, id: teeId }
    })
    return _.get('data.deleteTee', response)
  } catch (error) {
    return { error }
  }
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

export function * loadTeesFlow ({ payload }) {
  const tees = yield call(loadTeesEffect, payload)
  if (tees.error) {
    yield put(teesActions.loadError(tees))
  } else {
    yield put(teesActions.loadSuccess(tees))
  }
  yield put(teesActions.loadRequestDone())
}

export function * createTeesFlow ({ payload }) {
  const tees = yield call(createTeeEffect, payload)
  if (tees.error) {
    yield put(teesActions.createError(tees))
  } else {
    yield put(teesActions.createSuccess(tees))
  }
  yield put(teesActions.loadRequest())
  yield put(teesActions.createDone())
}

export function * saveTeesFlow ({ payload }) {
  const tees = yield call(saveTeesEffect, payload)
  if (tees.error) {
    yield put(teesActions.saveError(tees))
  } else {
    yield put(teesActions.saveSuccess(tees))
  }
  yield put(teesActions.saveDone())
}

export function * deleteTeesFlow ({ payload: teeId }) {
  const tees = yield call(deleteTeeEffect, teeId)
  if (tees.error) {
    yield put(teesActions.deleteError(tees))
  } else {
    yield put(teesActions.deleteSuccess(teeId))
  }
  yield put(teesActions.deleteDone())
}

export default function * () {
  return yield all([
    takeLatest(teesTypes.LOAD_REQUEST, loadTeesFlow),
    takeLatest(teesTypes.CREATE_REQUEST, createTeesFlow),
    takeLatest(teesTypes.SAVE_REQUEST, saveTeesFlow),
    takeLatest(teesTypes.DELETE_REQUEST, deleteTeesFlow)
  ])
}
